import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Typography, 
  Modal, 
  ModalDialog, 
  ModalClose, 
  FormControl, 
  FormLabel, 
  IconButton,
  Grid,
  Card,
  Avatar,
  Chip,
  Textarea
} from '@mui/joy';
import { supabase } from '../../supabaseClient';
import StyledTable from '../../components/StyledTable';
import EnhancedButton from '../../components/EnhancedButton';
import EnhancedInput from '../../components/EnhancedInput';
import MetricCard from '../../components/MetricCard';
import LoadingSkeleton, { CardSkeleton } from '../../components/LoadingSkeleton';

const ClientManager = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    company_website: '',
    address: '',
    notes: '',
  });

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    setLoading(true);
    // Fetch clients with count of associated projects and GIS projects
    const { data, error } = await supabase
      .from('clients')
      .select(`
        *,
        projects:projects(client_id),
        gis_projects:gis_projects(client_id)
      `)
      .returns('json');
    if (!error) {
      // Map clients to include project counts
      const clientsWithProjectCount = data.map(client => ({
        ...client,
        project_count: client.projects ? client.projects.length : 0,
        gis_project_count: client.gis_projects ? client.gis_projects.length : 0,
      }));
      setClients(clientsWithProjectCount);
    }
    setLoading(false);
  };

  const handleOpenDialog = (client = null) => {
    if (client) {
      setEditingClient(client);
      setFormData({
        name: client.name || '',
        contact_name: client.contact_name || '',
        contact_email: client.contact_email || '',
        contact_phone: client.contact_phone || '',
        company_website: client.company_website || '',
        address: client.address || '',
        notes: client.notes || '',
      });
    } else {
      setEditingClient(null);
      setFormData({
        name: '',
        contact_name: '',
        contact_email: '',
        contact_phone: '',
        company_website: '',
        address: '',
        notes: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingClient(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveClient = async () => {
    if (editingClient) {
      // Update client
      const { error } = await supabase
        .from('clients')
        .update(formData)
        .eq('id', editingClient.id);
      if (!error) {
        fetchClients();
        handleCloseDialog();
      }
    } else {
      // Insert new client
      const { error } = await supabase
        .from('clients')
        .insert([formData]);
      if (!error) {
        fetchClients();
        handleCloseDialog();
      }
    }
  };

  const handleDeleteClient = async (clientId) => {
    if (window.confirm('Are you sure you want to delete this client?')) {
      const { error } = await supabase
        .from('clients')
        .delete()
        .eq('id', clientId);
      if (!error) {
        fetchClients();
      }
    }
  };

  // Helper functions
  const getClientInitials = (clientName) => {
    return clientName?.split(' ').map(word => word[0]).join('').toUpperCase().substring(0, 2) || 'CL';
  };

  const getClientColor = (clientName) => {
    const colors = ['#00A884', '#007E33', '#17a2b8', '#6c757d', '#28a745', '#ffc107', '#dc3545'];
    const index = (clientName?.length || 0) % colors.length;
    return colors[index];
  };

  // Statistics
  const totalClients = clients.length;
  const totalProjects = clients.reduce((sum, c) => sum + c.project_count, 0);
  const totalGISProjects = clients.reduce((sum, c) => sum + c.gis_project_count, 0);
  const activeClients = clients.filter(c => c.project_count > 0).length;

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        {/* Analytics Loading */}
        <Box sx={{ mb: 4 }}>
          <LoadingSkeleton height="32px" width="200px" sx={{ mb: 3 }} />
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid xs={12} sm={6} md={3}><CardSkeleton /></Grid>
            <Grid xs={12} sm={6} md={3}><CardSkeleton /></Grid>
            <Grid xs={12} sm={6} md={3}><CardSkeleton /></Grid>
            <Grid xs={12} sm={6} md={3}><CardSkeleton /></Grid>
          </Grid>
        </Box>
        
        {/* Table Loading */}
        <CardSkeleton />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Client Analytics */}
      <Box sx={{ mb: 4 }}>
        <Typography 
          level="h4" 
          sx={{ 
            mb: 3, 
            color: 'success.700',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5
          }}
          className="fade-in"
        >
          <span className="material-symbols-outlined" style={{ fontSize: '24px', fontFamily: 'Material Symbols Outlined' }}>
            business
          </span>
          Client Analytics
        </Typography>
        
        <Grid container spacing={3} sx={{ mb: 4 }} className="fade-in-up">
          <Grid xs={12} sm={6} md={3}>
            <MetricCard
              title="Total Clients"
              value={totalClients}
              icon="business"
              gradientColors={['#00A884', '#007E33']}
            />
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <MetricCard
              title="Active Clients"
              value={activeClients}
              icon="trending_up"
              gradientColors={['#28a745', '#218838']}
            />
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <MetricCard
              title="Total Projects"
              value={totalProjects}
              icon="folder"
              gradientColors={['#17a2b8', '#138496']}
            />
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <MetricCard
              title="GIS Projects"
              value={totalGISProjects}
              icon="map"
              gradientColors={['#6c757d', '#5a6268']}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Add Client Section */}
      <Card 
        sx={{ 
          p: 3, 
          mb: 4,
          background: 'linear-gradient(135deg, rgba(0, 168, 132, 0.02) 0%, rgba(0, 126, 51, 0.02) 100%)',
          border: '1px solid',
          borderColor: 'success.200',
        }}
        className="fade-in"
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography 
              level="h4" 
              sx={{ 
                color: 'success.700',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                mb: 1
              }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: '24px', fontFamily: 'Material Symbols Outlined' }}>
                domain_add
              </span>
              Client Management
            </Typography>
            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
              Create, edit, and manage client information and contacts
            </Typography>
          </Box>
          
          <EnhancedButton
            gradient
            onClick={() => handleOpenDialog()}
            startDecorator={
              <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                add
              </span>
            }
          >
            Add Client
          </EnhancedButton>
        </Box>
      </Card>

      {/* Clients Table */}
      <Box className="fade-in-up">
        <Typography 
          level="h4" 
          sx={{ 
            mb: 3, 
            color: 'success.700',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5
          }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
            corporate_fare
          </span>
          Client Directory ({totalClients})
        </Typography>
        
        <StyledTable
          columns={[
            { key: 'client_info', label: 'Client', width: 300 },
            { key: 'contact_info', label: 'Contact Information', width: 250 },
            { key: 'projects', label: 'Projects', width: 150 },
            { key: 'address', label: 'Address', width: 200 },
            { key: 'actions', label: 'Actions', width: 120 }
          ]}
          rows={clients.map((client) => ({
            client_info: (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar 
                  sx={{ 
                    bgcolor: getClientColor(client.name),
                    color: 'white',
                    fontWeight: 700,
                    fontSize: '0.875rem'
                  }}
                >
                  {getClientInitials(client.name)}
                </Avatar>
                <Box>
                  <Typography level="title-sm" sx={{ color: 'success.700' }}>
                    {client.name || 'Unnamed Client'}
                  </Typography>
                  {client.company_website && (
                    <Typography level="body-xs" sx={{ color: 'primary.500' }}>
                      🌐 {client.company_website}
                    </Typography>
                  )}
                </Box>
              </Box>
            ),
            contact_info: (
              <Box>
                <Typography level="body-sm" sx={{ fontWeight: 500 }}>
                  {client.contact_name || 'No Contact Name'}
                </Typography>
                {client.contact_email && (
                  <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                    📧 {client.contact_email}
                  </Typography>
                )}
                {client.contact_phone && (
                  <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                    📞 {client.contact_phone}
                  </Typography>
                )}
              </Box>
            ),
            projects: (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Chip size="sm" variant="soft" color="success">
                  {client.project_count || 0} Projects
                </Chip>
                {client.gis_project_count > 0 && (
                  <Chip size="sm" variant="soft" color="primary">
                    {client.gis_project_count} GIS
                  </Chip>
                )}
              </Box>
            ),
            address: (
              <Typography level="body-sm">
                {client.address || 'No address provided'}
              </Typography>
            ),
            actions: (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <IconButton 
                  size="sm" 
                  variant="outlined" 
                  onClick={() => handleOpenDialog(client)}
                  sx={{ '&:hover': { borderColor: 'success.500', color: 'success.600' }}}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: '16px', fontFamily: 'Material Symbols Outlined' }}>
                    edit
                  </span>
                </IconButton>
                <IconButton 
                  size="sm" 
                  color="danger" 
                  variant="outlined" 
                  onClick={() => handleDeleteClient(client.id)}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: '16px', fontFamily: 'Material Symbols Outlined' }}>
                    delete
                  </span>
                </IconButton>
              </Box>
            )
          }))}
        />
      </Box>

      {/* Enhanced Client Modal */}
      <Modal open={openDialog} onClose={handleCloseDialog}>
        <ModalDialog 
          sx={{ 
            maxWidth: '700px',
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
            backdropFilter: 'blur(15px)',
            border: '1px solid',
            borderColor: 'success.200',
          }}
        >
          <ModalClose />
          <Typography 
            level="h4" 
            sx={{ 
              mb: 3,
              color: 'success.700',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: '24px', fontFamily: 'Material Symbols Outlined' }}>
              {editingClient ? 'edit' : 'domain_add'}
            </span>
            {editingClient ? 'Edit Client' : 'Add New Client'}
          </Typography>
          
          <Grid container spacing={2}>
            <Grid xs={12}>
              <EnhancedInput
                label="Client Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="ABC Corporation"
                required
                startDecorator={
                  <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                    business
                  </span>
                }
              />
            </Grid>
            
            <Grid xs={12} sm={6}>
              <EnhancedInput
                label="Contact Name"
                name="contact_name"
                value={formData.contact_name}
                onChange={handleChange}
                placeholder="John Smith"
                startDecorator={
                  <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                    person
                  </span>
                }
              />
            </Grid>
            
            <Grid xs={12} sm={6}>
              <EnhancedInput
                label="Contact Email"
                name="contact_email"
                type="email"
                value={formData.contact_email}
                onChange={handleChange}
                placeholder="john@company.com"
                startDecorator={
                  <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                    email
                  </span>
                }
              />
            </Grid>
            
            <Grid xs={12} sm={6}>
              <EnhancedInput
                label="Contact Phone"
                name="contact_phone"
                value={formData.contact_phone}
                onChange={handleChange}
                placeholder="(555) 123-4567"
                startDecorator={
                  <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                    phone
                  </span>
                }
              />
            </Grid>
            
            <Grid xs={12} sm={6}>
              <EnhancedInput
                label="Company Website"
                name="company_website"
                value={formData.company_website}
                onChange={handleChange}
                placeholder="https://company.com"
                startDecorator={
                  <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                    language
                  </span>
                }
              />
            </Grid>
            
            <Grid xs={12}>
              <EnhancedInput
                label="Address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="123 Main Street, City, State 12345"
                startDecorator={
                  <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                    location_on
                  </span>
                }
              />
            </Grid>
            
            <Grid xs={12}>
              <Box sx={{ mb: 2 }}>
                <Typography level="body-sm" sx={{ mb: 1, fontWeight: 500 }}>Notes</Typography>
                <Textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  placeholder="Additional notes about the client..."
                  minRows={3}
                  sx={{
                    '&:focus-within': {
                      borderColor: 'success.500',
                      boxShadow: '0 0 0 2px rgba(0, 168, 132, 0.25)',
                    },
                  }}
                />
              </Box>
            </Grid>
            
            <Grid xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                <EnhancedButton
                  variant="outlined"
                  onClick={handleCloseDialog}
                >
                  Cancel
                </EnhancedButton>
                <EnhancedButton
                  gradient
                  onClick={handleSaveClient}
                  startDecorator={
                    <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                      {editingClient ? 'save' : 'add'}
                    </span>
                  }
                >
                  {editingClient ? 'Save Changes' : 'Add Client'}
                </EnhancedButton>
              </Box>
            </Grid>
          </Grid>
        </ModalDialog>
      </Modal>
    </Box>
  );
};

export default ClientManager;